<script setup lang="ts">
import type { Component } from 'vue'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as icons from 'lucide-vue-next'
import { APP_MENU } from '@/config/app'
import NavUser from '@/components/NavUser.vue'
import TeamSwitcher from '@/components/TeamSwitcher.vue'
import { useUserStore } from '@/stores/user'

const props = defineProps<{
  collapsed?: boolean
}>()

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

// 从 APP_MENU 中提取所有有效的路径集合
const getAllowedPaths = (): Set<string> => {
  const paths = new Set<string>()
  
  Object.values(APP_MENU).forEach(group => {
    group.routes.forEach(route => {
      // 添加父菜单路径（如果有）
      if (route.path) {
        paths.add(route.path)
      }
      // 添加子菜单路径
      if (route.children) {
        route.children.forEach(child => {
          if (child.path) {
            paths.add(child.path)
          }
        })
      }
    })
  })
  
  return paths
}

// 将字符串图标名转换为组件
const getIcon = (iconName: string): Component | undefined => {
  if (!iconName) return undefined
  const icon = (icons as Record<string, any>)[iconName]
  return icon
}

// 递归转换菜单项为侧边栏格式（根据 APP_MENU 过滤）
const convertMenuItem = (item: any, allowedPaths: Set<string>): {
  title: string
  path?: string
  icon?: Component
  isActive?: boolean
  items?: Array<{ title: string; path?: string }>
} | null => {
  const icon = getIcon(item.icon)
  const fullPath = item.path || undefined
  
  // 如果有子菜单，先过滤子菜单
  if (item.children && item.children.length > 0) {
    const filteredChildren = item.children
      .map((child: any) => convertMenuItem(child, allowedPaths))
      .filter((child: any) => child !== null) as Array<{ title: string; path?: string }>
    
    // 如果所有子菜单都被过滤掉了，则整个父菜单也不显示
    if (filteredChildren.length === 0) {
      return null
    }
    
    // 对于有子菜单的父菜单：
    // 1. 如果父菜单有路径且在允许列表中，显示父菜单（带路径）
    // 2. 如果父菜单没有路径，只要有子菜单在允许列表中就显示（作为容器）
    // 3. 如果父菜单有路径但不在允许列表中，但子菜单在，仍然显示父菜单（作为容器，但不带路径）
    const isParentPathAllowed = fullPath ? allowedPaths.has(fullPath) : true
    const isActive = fullPath && isParentPathAllowed ? route.path === fullPath : false
    
    return {
      title: item.title,
      path: isParentPathAllowed ? fullPath : undefined, // 如果父菜单路径不在允许列表中，不显示路径
      icon,
      isActive,
      items: filteredChildren
    }
  } else {
    // 没有子菜单，检查路径是否在允许列表中
    if (fullPath && !allowedPaths.has(fullPath)) {
      return null
    }
    
    const isActive = fullPath ? route.path === fullPath : false
    return {
      title: item.title,
      path: fullPath,
      icon,
      isActive,
    }
  }
}

// 将后端返回的菜单树转换为侧边栏格式（去重并过滤）
const convertMenuToSidebarFormat = (menu: any, allowedPaths: Set<string>): Array<{
  title: string
  path?: string
  icon?: Component
  isActive?: boolean
  items?: Array<{ title: string; path?: string }>
}> => {
  if (!menu || !Array.isArray(menu)) return []
  
  // 使用 Map 去重，保留有子菜单的版本
  const menuMap = new Map<string | number, any>()
  
  menu.forEach((item: any) => {
    const key = item.id || item.title
    const converted = convertMenuItem(item, allowedPaths)
    
    // 如果转换后为 null，说明菜单被过滤掉了，跳过
    if (!converted) {
      return
    }
    
    const existing = menuMap.get(key)
    
    // 如果已存在，优先保留有子菜单的版本
    if (existing) {
      // 如果新转换的有子菜单，而现有的没有，则替换
      if (converted.items && converted.items.length > 0 && (!existing.items || existing.items.length === 0)) {
        menuMap.set(key, converted)
      }
      // 如果新转换的没有子菜单，但现有的有，则保留现有的
      // 如果两者都有子菜单或都没有，保留现有的（避免重复处理）
    } else {
      menuMap.set(key, converted)
    }
  })
  
  // 最终过滤：移除所有没有子菜单且没有路径的父菜单（空父菜单）
  return Array.from(menuMap.values()).filter(item => {
    // 如果有子菜单，保留
    if (item.items && item.items.length > 0) {
      return true
    }
    // 如果没有子菜单但有路径，保留
    if (item.path) {
      return true
    }
    // 既没有子菜单也没有路径，过滤掉
    return false
  })
}

// 将后端菜单按分组组织（处理树形结构）
const organizeMenusByGroup = (menus: any[]) => {
  const groups: Record<string, { name: string; items: any[] }> = {}
  const addedMenuIds = new Set<number>() // 用于跟踪已添加的菜单ID，避免重复
  
  // 递归处理菜单树
  const processMenu = (menu: any) => {
    const groupKey = menu.group || 'other'
    const groupName = menu.group === 'main' ? '核心' : menu.group === 'settings' ? '通用' : '其他'
    
    if (!groups[groupKey]) {
      groups[groupKey] = {
        name: groupName,
        items: []
      }
    }
    
    // 只添加顶级菜单（parentId === 0 或没有 parentId），且未添加过
    if ((!menu.parentId || menu.parentId === 0) && !addedMenuIds.has(menu.id)) {
      groups[groupKey].items.push(menu)
      addedMenuIds.add(menu.id)
    }
    
    // 递归处理子菜单（子菜单会保留在父菜单的 children 中）
    if (menu.children && menu.children.length > 0) {
      menu.children.forEach((child: any) => processMenu(child))
    }
  }
  
  menus.forEach((menu: any) => processMenu(menu))
  
  return Object.values(groups)
}

// 使用用户菜单或回退到 APP_MENU
const menuGroups = computed(() => {
  // 如果是超级管理员，直接显示所有菜单（APP_MENU）
  if (userStore.isSuperAdmin) {
    return Object.entries(APP_MENU).map(([, value]) => {
      const items: Array<{
        title: string
        path?: string
        icon?: Component
        isActive?: boolean
        items?: Array<{ title: string; path?: string }>
      }> = []

      value.routes.forEach((routeItem) => {
        const icon = getIcon(routeItem.icon)
        // 处理路径：如果 path 以 / 开头，直接使用；否则添加 /dashboard/ 前缀
        const fullPath = routeItem.path 
          ? (routeItem.path.startsWith('/') ? routeItem.path : `/dashboard/${routeItem.path}`)
          : undefined
        const isActive = fullPath ? route.path === fullPath : false
        
        if (routeItem.children && routeItem.children.length > 0) {
          items.push({
            title: routeItem.title,
            path: fullPath,
            icon,
            isActive,
            items: routeItem.children.map((child) => {
              const childPath = child.path 
                ? (child.path.startsWith('/') ? child.path : `/dashboard/${child.path}`)
                : undefined
              return {
                title: child.title,
                path: childPath,
              }
            }),
          })
        } else {
          items.push({
            title: routeItem.title,
            path: fullPath,
            icon,
            isActive,
          })
        }
      })

      return {
        name: value.name,
        items,
      }
    })
  }
  
  // 普通用户：如果用户有菜单数据，使用用户菜单
  const userMenus = userStore.userMenus
  if (userMenus && userMenus.length > 0) {
    const allowedPaths = getAllowedPaths()
    const organizedGroups = organizeMenusByGroup(userMenus)
    return organizedGroups.map(group => ({
      name: group.name,
      items: convertMenuToSidebarFormat(group.items, allowedPaths)
    })).filter(group => group.items.length > 0) // 过滤掉空的分组
  }
  
  // 否则使用硬编码的 APP_MENU（兼容旧逻辑）
  return Object.entries(APP_MENU).map(([, value]) => {
    const items: Array<{
      title: string
      path?: string
      icon?: Component
      isActive?: boolean
      items?: Array<{ title: string; path?: string }>
    }> = []

    value.routes.forEach((routeItem) => {
      const icon = getIcon(routeItem.icon)
      // 处理路径：如果 path 以 / 开头，直接使用；否则添加 /dashboard/ 前缀
      const fullPath = routeItem.path 
        ? (routeItem.path.startsWith('/') ? routeItem.path : `/dashboard/${routeItem.path}`)
        : undefined
      const isActive = fullPath ? route.path === fullPath : false
      
      if (routeItem.children && routeItem.children.length > 0) {
        items.push({
          title: routeItem.title,
          path: fullPath,
          icon,
          isActive,
          items: routeItem.children.map((child) => {
            const childPath = child.path 
              ? (child.path.startsWith('/') ? child.path : `/dashboard/${child.path}`)
              : undefined
            return {
              title: child.title,
              path: childPath,
            }
          }),
        })
      } else {
        items.push({
          title: routeItem.title,
          path: fullPath,
          icon,
          isActive,
        })
      }
    })

    return {
      name: value.name,
      items,
    }
  })
})

// 用户数据
import { API_BASE_URL } from '@/config/api'
const userData = computed(() => {
  let avatarUrl = userStore.userAvatar || ''
  if (avatarUrl && !avatarUrl.startsWith('http')) {
    avatarUrl = `${API_BASE_URL}${avatarUrl}`
  }
  return {
    name: userStore.userName || 'User',
    email: userStore.userGonghao || userStore.userDepartment || 'user@example.com',
    avatar: avatarUrl,
  }
})

// 团队数据（示例数据，可以根据需要修改）
const teamsData = [
  {
    name: "Oafull",
    logo: icons.Boxes,
    plan: "Enterprise",
  },
]

// 当前激活的菜单索引
const activeMenuIndex = computed(() => {
  for (let groupIndex = 0; groupIndex < menuGroups.value.length; groupIndex++) {
    const group = menuGroups.value[groupIndex]
    for (let itemIndex = 0; itemIndex < group.items.length; itemIndex++) {
      const item = group.items[itemIndex]
      if (item.path === route.path) {
        return `${groupIndex}-${itemIndex}`
      }
      if (item.items) {
        for (let subIndex = 0; subIndex < item.items.length; subIndex++) {
          if (item.items[subIndex].path === route.path) {
            return `${groupIndex}-${itemIndex}-${subIndex}`
          }
        }
      }
    }
  }
  return ''
})

// 处理菜单点击（只处理子菜单的导航，父菜单点击只展开/收起，不导航）
const handleMenuSelect = (index: string) => {
  const parts = index.split('-')
  const groupIndex = parseInt(parts[0])
  const itemIndex = parseInt(parts[1])
  const subIndex = parts[2] ? parseInt(parts[2]) : undefined
  
  if (groupIndex >= 0 && itemIndex >= 0) {
    const group = menuGroups.value[groupIndex]
    if (group && group.items[itemIndex]) {
      const item = group.items[itemIndex]
      // 只有当点击的是子菜单项时才导航
      if (subIndex !== undefined && item.items && item.items[subIndex]) {
        const subItem = item.items[subIndex]
        if (subItem.path) {
          router.push(subItem.path)
        }
      }
      // 父菜单点击时不导航，只展开/收起（由 Element Plus 默认行为处理）
    }
  }
}
</script>

<template>
  <div class="h-full flex flex-col" :class="collapsed ? 'w-16' : 'w-64'" style="background-color: hsl(var(--sidebar)); color: hsl(var(--sidebar-foreground));">
    <!-- Header -->
    <div class="p-4 border-b" style="border-color: hsl(var(--sidebar-border));">
      <TeamSwitcher :teams="teamsData" :collapsed="collapsed" />
    </div>
    
    <!-- Menu Content -->
    <el-scrollbar class="flex-1">
      <el-menu
        :default-active="activeMenuIndex"
        :collapse="collapsed"
        :collapse-transition="false"
        class="border-none"
        style="background-color: transparent;"
        @select="handleMenuSelect"
      >
        <template v-for="(group, groupIndex) in menuGroups" :key="group.name">
          <el-menu-item v-if="group.name && !collapsed" disabled class="!text-xs !h-8 !leading-8 !cursor-default opacity-70">
            {{ group.name }}
          </el-menu-item>
          <template v-for="(item, itemIndex) in group.items" :key="item.title">
            <el-sub-menu v-if="item.items && item.items.length > 0" :index="`${groupIndex}-${itemIndex}`">
              <template #title>
                <div style="display: flex; align-items: center; gap: 8px; width: 100%;">
                  <el-icon v-if="item.icon">
                    <component :is="item.icon" />
                  </el-icon>
                  <span>{{ item.title }}</span>
                </div>
              </template>
              <el-menu-item
                v-for="(subItem, subIndex) in item.items"
                :key="subItem.title"
                :index="`${groupIndex}-${itemIndex}-${subIndex}`"
                @click="subItem.path && router.push(subItem.path)"
              >
                {{ subItem.title }}
              </el-menu-item>
            </el-sub-menu>
            <el-menu-item
              v-else
              :index="`${groupIndex}-${itemIndex}`"
              @click="item.path && router.push(item.path)"
            >
              <el-icon v-if="item.icon">
                <component :is="item.icon" />
              </el-icon>
              <template #title>{{ item.title }}</template>
            </el-menu-item>
          </template>
        </template>
      </el-menu>
    </el-scrollbar>
    
    <!-- Footer -->
    <div class="p-4 border-t" style="border-color: hsl(var(--sidebar-border));">
      <NavUser :user="userData" :collapsed="collapsed" />
    </div>
  </div>
</template>

<style scoped>
:deep(.el-menu) {
  background-color: transparent;
  border: none;
}

:deep(.el-menu-item) {
  color: hsl(var(--sidebar-foreground));
}

:deep(.el-menu-item:hover) {
  background-color: hsl(var(--sidebar-accent));
  color: hsl(var(--sidebar-accent-foreground));
}

:deep(.el-menu-item.is-active) {
  background-color: hsl(var(--sidebar-accent));
  color: hsl(var(--sidebar-accent-foreground));
}

:deep(.el-sub-menu__title) {
  color: hsl(var(--sidebar-foreground));
}

:deep(.el-sub-menu__title:hover) {
  background-color: hsl(var(--sidebar-accent));
  color: hsl(var(--sidebar-accent-foreground));
}

:deep(.el-menu-item.is-disabled) {
  opacity: 0.7;
  cursor: default;
}

:deep(.el-menu--collapse) {
  width: 64px;
}

:deep(.el-menu--collapse .el-menu-item),
:deep(.el-menu--collapse .el-sub-menu__title) {
  padding: 0 20px !important;
}
</style>
